import { Button } from '@material-ui/core';
import { ErrorAPI } from 'api/Associado/methods';
import { AxiosError } from 'axios';
import AbrirVotacaoPautaModal, { IAbrirVotacaoPautaModalGenericProps } from 'components/AbrirVotacaoPautaModal/AbrirVotacaoPautaModal';
import CadastroPautaModal, { ICadastroPautaModalProps } from 'components/CadastroPautaModal/CadastroPautaModal';
import ConfirmDeletarPautaModal, { IConfirmDeletarPautaModalProps } from 'components/ConfirmDeletarPautaModal/ConfirmDeletarPautaModal';
import { AssembleiaIcon, ChartIcon, CircleCloseIcon, EditIcon, PresentationIcon, SyncIcon, TrashIcon } from 'components/Icons';
import ResultadosModal, { ResultadosModalProps }  from 'components/ResultadosModal';
import { AssembleiaPautaStatus } from 'enuns/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'enuns/AssembleiaStatus';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { assembleiasAssessorActionsAlterarSituacaoPauta, assembleiasAssessorActionsLiberarResultadoPauta } from 'store/modules/AssembleiasAssessorActions';
import { IItensCadastrarPautaRequest } from 'store/modules/AssembleiasAssessorReducer';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import ErrorHandler from 'utils/ErrorHandler';
import { getUniqueKey } from 'utils/KeyUtils';
import { IPauta } from './AssembleiaPautas';
import useStyles from './style';


export interface IAssembleiaPautaAssessorActionButtons {
  loading: boolean,
  setLoading: Function,
  pauta: IPauta,
  voto: number
}

export default ({
  loading,
  setLoading,
  pauta,
  voto
}: IAssembleiaPautaAssessorActionButtons) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [sendingAlteracaoStatus, setSendingAlteracaoStatus] = useState<boolean>(false);
  const dispatchAction = useDispatch();
  const currentEvent = useSelector((state: IReducer) => state.assembleiasAssessor.evento);
  const usuario = useSelector((state: IReducer) => state.auth.usuario);
  const showError = useCallback((error: AxiosError<ErrorAPI>) => {
    setSendingAlteracaoStatus(false);
    ErrorHandler(error.response!.data);
  }, [setSendingAlteracaoStatus])

  const encerrarVotacao = useCallback(() => {
    setSendingAlteracaoStatus(true);
    dispatchAction(
      assembleiasAssessorActionsAlterarSituacaoPauta(
        pauta.idPauta,
        0,
        AssembleiaPautaStatus.Encerrada,
        () => setSendingAlteracaoStatus(false),
        showError
      )
    )
  }, [dispatchAction, setSendingAlteracaoStatus, pauta, showError])

  const abrirVotacao = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatchAction(
      modalsActionsOpen(
        AbrirVotacaoPautaModal,
        {
          genericProps: {
            key: modalKey,
            pauta: pauta
          } as IAbrirVotacaoPautaModalGenericProps
        },
        modalKey
      )
    );
  }, [dispatchAction, pauta])

  const trocaStatusOnClick = useCallback(() => {
    switch(pauta.status) {
      case AssembleiaPautaStatus.Liberada:
        return encerrarVotacao();
      default:
        return abrirVotacao();
    }
  }, [pauta, encerrarVotacao, abrirVotacao])

  const tocaStatusIcon = useMemo(() => {
    switch(pauta.status) {
      case AssembleiaPautaStatus.Liberada:
        return <CircleCloseIcon />;
      case AssembleiaPautaStatus.Bloqueada:
      case AssembleiaPautaStatus.Encerrada:
      default:
        return <AssembleiaIcon />;
    }
  }, [pauta])

  const tocaStatusLabel = useMemo(() => {
    switch(pauta.status) {
      case AssembleiaPautaStatus.Bloqueada:
        return t('AssembleiaPauta.acoes.liberarVotacao');
      case AssembleiaPautaStatus.Liberada:
        return t('AssembleiaPauta.acoes.encerrarVotacao');
      case AssembleiaPautaStatus.Encerrada:
      default:
        return t('AssembleiaPauta.acoes.reabrirVotacao');
    }
  }, [pauta, t])

  const mostrarTrocaStatusButton = useMemo(() => {
    return !!(
      currentEvent &&
      currentEvent?.status === AssembleiaStatus.Live &&
      usuario!.perfil.assessorRoute.assembleias.pautas.alterarSituacao &&
      pauta.itens.length > 0
    )
  }, [currentEvent, pauta, usuario])

  const trocaStatusButton = useMemo(() => {
    return mostrarTrocaStatusButton && !pauta.flgResultadoLiberado && (
      <Button
        key={5}
        className={ classes.botaoAcoes }
        variant="contained"
        color="primary"
        onClick={ trocaStatusOnClick }
        startIcon={ tocaStatusIcon }
        endIcon={ sendingAlteracaoStatus ? <SyncIcon className="animation-rotate" /> : null }
      >
        { tocaStatusLabel }
      </Button>
    )
  }, [mostrarTrocaStatusButton, classes, pauta, trocaStatusOnClick, tocaStatusIcon, sendingAlteracaoStatus, tocaStatusLabel])

  const mostrarVisualizarResultadosButton = useMemo(() => {
    return !!(
      currentEvent &&
      (
        currentEvent.status === AssembleiaStatus.Live ||
        currentEvent.status === AssembleiaStatus.Pos ||
        currentEvent.status === AssembleiaStatus.Encerrado
      ) &&
      pauta.status === AssembleiaPautaStatus.Encerrada
    )
  }, [currentEvent, pauta])

  const openResultadosModal = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatchAction(
      modalsActionsOpen(
        ResultadosModal,
        {
          genericProps: {
            key: modalKey,
            idPauta: pauta.idPauta,
            flgAssessor: true
          } as ResultadosModalProps,
        },
        modalKey
      )
    );
  }, [dispatchAction, pauta]);

  const visualizarResultadosButton = useMemo(() => {
    return mostrarVisualizarResultadosButton && (
      <Button
        key={0}
        className={[
          classes.botaoAcoes,
          classes.botaoVerResultados
        ].join(' ')}
        variant="outlined"
        color="primary"
        startIcon={ <PresentationIcon /> }
        onClick={openResultadosModal}
        disabled={pauta.status !== AssembleiaPautaStatus.Encerrada}
      >
        {t(`AssembleiaPauta.acoes.visualizarResultados`)}
      </Button>
    )
  }, [mostrarVisualizarResultadosButton, openResultadosModal, classes, t, pauta])

  const mostratLiberarResultadosButton = useMemo(() => {
    return mostrarVisualizarResultadosButton && !pauta.flgResultadoLiberado;
  }, [mostrarVisualizarResultadosButton, pauta])

  const toggleResultado = useCallback(() => {
    setSendingAlteracaoStatus(true);
    dispatchAction(
      assembleiasAssessorActionsLiberarResultadoPauta(
        pauta.idPauta,
        !pauta.flgResultadoLiberado,
        () => setSendingAlteracaoStatus(false),
        showError
      )
    )
  }, [dispatchAction, pauta, setSendingAlteracaoStatus, showError]);

  const liberarResultadosButton = useMemo(() => {
    return mostratLiberarResultadosButton && (
      <Button
        key={1}
        className={classes.botaoAcoes}
        variant="outlined"
        color="primary"
        startIcon={ <ChartIcon /> }
        onClick={toggleResultado}
        disabled={pauta.flgResultadoLiberado}
      >
        {t(`AssembleiaPauta.acoes.liberarResultados`)}
      </Button>
    )
  }, [mostratLiberarResultadosButton, classes, t, pauta, toggleResultado])

  const mostrarBloquearResultadosButton = useMemo(() => {
    return mostrarVisualizarResultadosButton && !!pauta.flgResultadoLiberado;
  }, [mostrarVisualizarResultadosButton, pauta])

  const bloquearResultadosButton = useMemo(() => {
    return mostrarBloquearResultadosButton && (
      <Button
        key={2}
        onClick={toggleResultado}
        className={[
          classes.botaoAcoes,
          classes.botaoBloquearResultados
        ].join(' ')}
        variant="outlined"
        color="primary"
        startIcon={ <CircleCloseIcon /> }
      >
        {t(`AssembleiaPauta.acoes.bloquearResultados`)}
      </Button>
    )
  }, [mostrarBloquearResultadosButton, classes, t, toggleResultado])

  const openEditarPautaModal = useCallback(() => {

    let itensEditarPauta: IItensCadastrarPautaRequest[] = [];

    pauta.itens.forEach(it => {
      let item: IItensCadastrarPautaRequest = {
        descricao: it.descricao,
        flgAbstencao: it.flgAbstencao,
        ordem: it.ordem
      }
      
      itensEditarPauta.push( item )
    })

    const modalKey: string = getUniqueKey();
    dispatchAction(
      modalsActionsOpen(
        CadastroPautaModal,
        {
          genericProps: {
            key: modalKey,
            idEvento: currentEvent?.id,
            status: currentEvent?.status,
            idPauta: pauta.idPauta,
            titulo: pauta.titulo,
            descricao: pauta.descricao,
            itens: itensEditarPauta,
            idsProdutos: pauta.idsProdutos,
            produtosAssembleia: currentEvent!.produtos
          } as ICadastroPautaModalProps
        },
        modalKey
      )
    );
  }, [pauta, dispatchAction, currentEvent])

  const mostraEditarPautaButton = useMemo(() => {
    return !!(
      currentEvent &&
      (
        currentEvent.status === AssembleiaStatus.NaoDisponivel ||
        currentEvent.status === AssembleiaStatus.Pre ||
        currentEvent.status === AssembleiaStatus.Live
      ) &&
      pauta.status === AssembleiaPautaStatus.Bloqueada &&
      usuario!.perfil.assessorRoute.assembleias.pautas.editar
    )
  }, [currentEvent, pauta, usuario])

  const editarPautaButton = useMemo(() => {
    return mostraEditarPautaButton && (
      <Button
        key={4}
        className={ classes.botaoEditar }
        variant="outlined"
        color="primary"
        onClick={ openEditarPautaModal }
        startIcon={<EditIcon />}
      >
        { t("EditarPauta.labelButton") }
      </Button>
    )
  }, [mostraEditarPautaButton, classes, t, openEditarPautaModal])

  const openConfirmDeletarPautaModal = useCallback(() => {

    const modalKey: string = getUniqueKey();
    dispatchAction(
      modalsActionsOpen(
        ConfirmDeletarPautaModal,
        {
          genericProps: {
            key: modalKey,
            idPauta: pauta.idPauta,
            idEvento: currentEvent?.id,
            titulo: pauta.titulo
          } as IConfirmDeletarPautaModalProps
        },
        modalKey
      )
    );
  }, [dispatchAction, pauta, currentEvent])

  const mostrarDeletarPautaButton = useMemo(() => {
    return !!(
      currentEvent &&
      (
        currentEvent.status === AssembleiaStatus.NaoDisponivel ||
        currentEvent.status === AssembleiaStatus.Pre ||
        currentEvent.status === AssembleiaStatus.Live
      ) &&
      pauta.status === AssembleiaPautaStatus.Bloqueada &&
      usuario!.perfil.assessorRoute.assembleias.pautas.deletar
    )
  }, [currentEvent, pauta, usuario])

  const deletarPautaButton = useMemo(() => {
    return mostrarDeletarPautaButton && (
      <Button
        key={3}
        className={ classes.botaoDeletar }
        variant="contained"
        color="primary"
        onClick={ openConfirmDeletarPautaModal }
        startIcon={<TrashIcon />}
      >
        { t("ConfirmDeletarPauta.labelButton") }
      </Button>
    )
  }, [mostrarDeletarPautaButton, classes, t, openConfirmDeletarPautaModal])

  const buttons = useMemo<ReactNode[]>(() => {
    return [
      visualizarResultadosButton,
      liberarResultadosButton,
      bloquearResultadosButton,
      deletarPautaButton,
      editarPautaButton,
      trocaStatusButton,
    ]
  }, [
    visualizarResultadosButton,
    liberarResultadosButton,
    bloquearResultadosButton,
    deletarPautaButton,
    editarPautaButton,
    trocaStatusButton,
  ])

  return buttons.filter(it => it !== null) ? (
    <div>
      {buttons.map(it => it)}
    </div>
  ) : null;
}